import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { FeeConfiguration } from '@/types';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import FeeEditor, { EditorFeeConfiguration } from '../FeeEditor';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';

interface FeeEditDialogProps {
    loader(): Promise<FeeConfiguration[]>;
    onClose(): void;
    onAccept(data: FeeConfiguration[]): Promise<any> | void;
    show: boolean;
    quantity: number;
    basePrice: number;
}

const FeeEditDialog: React.FC<FeeEditDialogProps> = ({ show, onClose, onAccept, loader, quantity, basePrice }) => {
    const [saving, setSaving] = useState(false);
    const [feeConfigurations, setFeeConfigurations] = useState<EditorFeeConfiguration[] | null>(null);

    const handleOpen = async () => {
        setSaving(false);
        setFeeConfigurations(null);
        setFeeConfigurations(await loader());
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAccept = async (e) => {
        setSaving(true);

        const data = feeConfigurations!.map((x) => ({
            ...x,
            taxPercentage: x.taxPercentage || 0,
            feePercentage: x.feePercentage || 0,
        }));

        try {
            await triggerHandler(onAccept, data as FeeConfiguration[]);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen} size="lg">
            <ModalHeader>
                <Trans>Edit fees and VAT</Trans>
            </ModalHeader>
            <ModalBody>
                {feeConfigurations ? (
                    <FeeEditor
                        value={feeConfigurations}
                        onChange={setFeeConfigurations}
                        previewQuantity={quantity}
                        previewBasePrice={basePrice}
                    />
                ) : (
                    <LoadingIndicator />
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleAccept} loading={saving}>
                    <Trans>Save</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default FeeEditDialog;
